import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import {
	useCommonStands,
	useMillSetupSheet,
	useProductSizes,
	useProductTypes,
	useRollsByStand,
	useStandSetup,
	useUpsertMillSetupSheet,
} from "../hooks/useMillSetupSheet";
import type {
	RollsByStandRow,
	StandSetupGridRow,
} from "../models/MillSetupSheet";
import { CommentBox } from "./CommentBox";
import { MillSetupSheetToolbar } from "./MillSetupSheetToolbar";
import { RollsByStandTable } from "./RollsByStandTable";
import { StandSetupTable } from "./StandSetupTable";

const PANEL_HEIGHT = 460;

const URL_SCHEMA = {
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
};

export const MillSetupSheetManager = () => {
	const [
		{ productTypeId: selectedTypeId, productSizeId: selectedSizeId },
		setUrlState,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [special, setSpecial] = useState("");
	const [comments, setComments] = useState("");
	// Last persisted values, to autosave on blur only when something changed.
	const savedComments = useRef({ special: "", comments: "" });

	const { data: commonStands = [] } = useCommonStands();
	const { data: productTypes = [], isSuccess: productTypesLoaded } =
		useProductTypes();
	const {
		data: productSizes = [],
		isFetching: loadingSizes,
		isSuccess: productSizesLoaded,
	} = useProductSizes(selectedTypeId);
	const { data: sheetData } = useMillSetupSheet(selectedSizeId);
	const { data: standRows = [] } = useStandSetup(selectedSizeId);
	const { data: rollsRows = [] } = useRollsByStand(selectedSizeId);

	const { mutate: saveSheet, isPending: isSaving } = useUpsertMillSetupSheet({
		onSuccess: () => setNotification("Mill setup sheet saved"),
		onError: (err) => setError(err.message),
	});

	useEffect(() => {
		const s = sheetData?.sheet?.SpecialComments ?? "";
		const c = sheetData?.sheet?.Comments ?? "";
		setSpecial(s);
		setComments(c);
		savedComments.current = { special: s, comments: c };
	}, [sheetData]);

	// Always render the full mill frame (all common stands), merging the
	// product's pass setup by StandId. Stands without data stay blank — the
	// page shows the structure even before a product is picked (legacy parity).
	const mergedStandRows = useMemo<StandSetupGridRow[]>(() => {
		const byStand = new Map(standRows.map((r) => [r.StandId, r]));
		return commonStands.map((s) => {
			const ps = byStand.get(s.Id);
			return {
				StandId: s.Id,
				StandName: s.Name,
				StandNumber: s.StandNumber,
				PassId: ps?.PassId ?? null,
				PassIdName: ps?.PassIdName ?? null,
				PassName: ps?.PassName ?? null,
				PassShapeName: ps?.PassShapeName ?? null,
				PassDepth: ps?.PassDepth ?? null,
				PassWidth: ps?.PassWidth ?? null,
				BarSize: ps?.BarSize ?? null,
				RollPart: ps?.RollPart ?? null,
				RolledAl: ps?.RolledAl ?? null,
				Comments: ps?.Comments ?? null,
			};
		});
	}, [commonStands, standRows]);

	const mergedRollsRows = useMemo<RollsByStandRow[]>(() => {
		const byStand = new Map(rollsRows.map((r) => [r.StandId, r]));
		return commonStands.map((s) => {
			const rr = byStand.get(s.Id);
			return {
				StandId: s.Id,
				StandName: s.Name,
				StandNumber: s.StandNumber,
				MustChange: rr?.MustChange ?? false,
				Diameter: rr?.Diameter ?? null,
				CartridgeName: rr?.CartridgeName ?? null,
			};
		});
	}, [commonStands, rollsRows]);

	// A shared link can carry a product that no longer exists, or a size of another type.
	useEffect(() => {
		if (!productTypesLoaded || selectedTypeId == null) return;
		if (!productTypes.some((t) => t.Id === selectedTypeId))
			dropValues("productTypeId", "productSizeId");
	}, [productTypesLoaded, productTypes, selectedTypeId, dropValues]);

	useEffect(() => {
		if (selectedSizeId == null) return;
		if (selectedTypeId == null) {
			dropValues("productSizeId");
			return;
		}
		if (
			productSizesLoaded &&
			!productSizes.some((s) => s.Id === selectedSizeId)
		)
			dropValues("productSizeId");
	}, [
		productSizesLoaded,
		productSizes,
		selectedTypeId,
		selectedSizeId,
		dropValues,
	]);

	const handleTypeChange = useCallback(
		(id: number | null) =>
			setUrlState({ productTypeId: id, productSizeId: null }),
		[setUrlState],
	);

	const handleSizeChange = useCallback(
		(id: number | null) => setUrlState({ productSizeId: id }),
		[setUrlState],
	);

	const handleClear = useCallback(
		() => setUrlState({ productTypeId: null, productSizeId: null }),
		[setUrlState],
	);

	const handleCommentsBlur = useCallback(() => {
		if (selectedSizeId == null) return;
		if (
			special === savedComments.current.special &&
			comments === savedComments.current.comments
		)
			return;
		savedComments.current = { special, comments };
		saveSheet({
			productSizeId: selectedSizeId,
			specialComments: special,
			comments,
		});
	}, [special, comments, selectedSizeId, saveSheet]);

	const noProduct = selectedSizeId == null;

	return (
		<Box sx={{ width: "100%", p: 1 }}>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Mill Setup Sheet
			</Typography>

			<MillSetupSheetToolbar
				productTypes={productTypes}
				productSizes={productSizes}
				selectedTypeId={selectedTypeId}
				selectedSizeId={selectedSizeId}
				header={sheetData?.header ?? null}
				loadingSizes={loadingSizes}
				onTypeChange={handleTypeChange}
				onSizeChange={handleSizeChange}
				onClear={handleClear}
			/>

			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12, md: 8 }}>
					<Paper
						elevation={3}
						sx={{
							p: 1,
							height: PANEL_HEIGHT,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 600, px: 0.5, pb: 0.5 }}
						>
							Stand Setup
						</Typography>
						<Box sx={{ flex: 1 }}>
							<StandSetupTable rows={mergedStandRows} />
						</Box>
					</Paper>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 4 }}>
					<Paper
						elevation={3}
						sx={{
							p: 1,
							height: PANEL_HEIGHT,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
								px: 0.5,
								pb: 0.5,
							}}
						>
							<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
								Roll Diameters by Stand
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
									<Box
										sx={{
											width: 16,
											height: 16,
											borderRadius: 0.5,
											backgroundColor: "#a5d6a7",
											border: "1.5px solid #66bb6a",
										}}
									/>
									<Typography
										variant="body2"
										sx={{ color: "text.secondary", fontSize: "13px" }}
									>
										Change rolls, available cartridge
									</Typography>
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
									<Box
										sx={{
											width: 16,
											height: 16,
											borderRadius: 0.5,
											backgroundColor: "#ef9a9a",
											border: "1.5px solid #ef5350",
										}}
									/>
									<Typography
										variant="body2"
										sx={{ color: "text.secondary", fontSize: "13px" }}
									>
										Change rolls, not available cartridge
									</Typography>
								</Box>
							</Box>
						</Box>
						<Box sx={{ flex: 1 }}>
							<RollsByStandTable rows={mergedRollsRows} />
						</Box>
					</Paper>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 8 }}>
					<CommentBox
						title="Special Comments"
						value={special}
						disabled={isSaving || noProduct}
						onChange={setSpecial}
						onBlur={handleCommentsBlur}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 4 }}>
					<CommentBox
						title="Comments"
						value={comments}
						disabled={isSaving || noProduct}
						onChange={setComments}
						onBlur={handleCommentsBlur}
					/>
				</Grid2>
			</Grid2>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
